import { api } from '../services/api';

// Single fixed key — no versioning needed.
// Cache invalidates automatically when the blob's Last-Modified header changes.
const CACHE_KEY = 'broker_detail_cache';

export interface BrokerEntry {
  code: string;
  name: string;
}

interface CachePayload {
  lastModified: string; // from Azure blob Last-Modified header
  brokers: BrokerEntry[];
}

function buildBlobUrl(path: string, containerUrl: string, sasToken: string): string {
  const isDev = (import.meta as any).env?.DEV || false;
  return isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;
}

/**
 * Parse a simple CSV (first row = header) into an array of objects.
 * Headers are normalised to lowercase so column name casing in the CSV doesn't matter.
 */
function parseSimpleCSV(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
  return lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = values[i] ?? ''; });
    return obj;
  });
}

/**
 * Fetch broker_detail.csv from blob.
 * Returns the parsed entries AND the Last-Modified header value so we can cache it.
 */
async function fetchBrokerDetailFromBlob(): Promise<{ brokers: BrokerEntry[]; lastModified: string }> {
  const tokenRes = await api.getStockMetadataSasToken();
  if (!tokenRes.success || !tokenRes.data) {
    throw new Error(tokenRes.error || 'Failed to get SAS token');
  }
  const { containerUrl, sasToken } = tokenRes.data;
  const url = buildBlobUrl('csv_input/broker_detail.csv', containerUrl, sasToken);

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch broker_detail.csv: ${res.status}`);

  const lastModified = res.headers.get('Last-Modified') ?? res.headers.get('last-modified') ?? '';
  const text = await res.text();
  const rows = parseSimpleCSV(text);

  const brokers = rows
    .filter(r => r['code'] && r['name'])
    .map(r => ({ code: r['code'].trim(), name: r['name'].trim() }));

  return { brokers, lastModified };
}

/**
 * Load brokers from localStorage cache.
 * - Serves cache immediately if it exists (no TTL).
 * - In the background, checks blob Last-Modified and replaces cache if the file changed.
 * - First load (no cache): fetches synchronously.
 */
export async function loadBrokerMap(): Promise<BrokerEntry[]> {
  // Try reading cache
  let cached: CachePayload | null = null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const payload: CachePayload = JSON.parse(raw);
      if (payload.brokers && payload.brokers.length > 0) {
        cached = payload;
      }
    }
  } catch {
    // corrupt cache — will re-fetch below
  }

  if (cached) {
    // Serve cached data immediately, then validate in background
    validateAndRefreshCache(cached.lastModified).catch(() => {/* silent */});
    return cached.brokers;
  }

  // No cache → fetch synchronously
  const { brokers, lastModified } = await fetchBrokerDetailFromBlob();
  saveCache({ brokers, lastModified });
  return brokers;
}

/**
 * Check if the blob has been modified since we last cached it.
 * If so, re-fetch and replace (overwrite) the cache with fresh data.
 */
async function validateAndRefreshCache(cachedLastModified: string): Promise<void> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) return;

    const { containerUrl, sasToken } = tokenRes.data;
    const url = buildBlobUrl('csv_input/broker_detail.csv', containerUrl, sasToken);

    // Lightweight check: use If-Modified-Since header so Azure returns 304 if unchanged
    const headRes = await fetch(url, {
      method: 'GET',
      headers: cachedLastModified ? { 'If-Modified-Since': cachedLastModified } : {},
    });

    if (headRes.status === 304) {
      // Not modified — cache still valid
      return;
    }

    if (!headRes.ok) return;

    // File has changed — parse response and overwrite cache
    const newLastModified = headRes.headers.get('Last-Modified') ?? headRes.headers.get('last-modified') ?? '';
    const text = await headRes.text();
    const rows = parseSimpleCSV(text);
    const brokers = rows
      .filter(r => r['code'] && r['name'])
      .map(r => ({ code: r['code'].trim(), name: r['name'].trim() }));

    saveCache({ brokers, lastModified: newLastModified });
    console.log('[brokerCache] CSV updated in Azure — cache replaced.');
  } catch {
    // Silent — background refresh failure is not critical
  }
}

function saveCache(payload: CachePayload): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage might be full — ignore
  }
}

/**
 * Given a "Nama Rekening Efek" string, return the broker code if any broker
 * name (case-insensitive contains) matches, otherwise return null.
 */
export function resolveBrokerCode(
  rekeningEfek: string,
  brokers: BrokerEntry[]
): string | null {
  if (!rekeningEfek) return null;
  const lower = rekeningEfek.toLowerCase();
  for (const b of brokers) {
    if (lower.includes(b.name.toLowerCase())) {
      return b.code;
    }
  }
  return null;
}
